import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, Typography } from '@mui/material';
import { FC, Fragment, JSX, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
// components
import { Alert } from 'components/common/Alert';
import DataLoader from 'components/common/DataLoader';
import FlavorForm from './components/FlavorForm';
import { BackdropLoader } from 'components/common/BackdropLoader';
// Schema, styles, graphql, constants
import { flexCenterBetween } from 'styles/commonComponentStyle';
import {
  SweetenerNatureType,
  SweetenerPayload,
  SweetenerSizeType,
  SweetenerType,
  useCreateFlavorMutation,
  useGetFlavorLazyQuery,
  useUpdateFlavorMutation,
} from 'generated/graphql';
import {
  HTTP_STATUS,
  sweetenerSystemInitialValue,
  FLAVOR_IS_UPDATED_TEXT,
  ADD_TEXT,
  FLAVOR_IS_CREATED_TEXT,
  ADD_FLAVOR_TEXT,
  EDIT_FLAVOR_TEXT,
} from 'constants/index';
import { AddSweetenerSystemFormType, ParamType } from 'interfaces';
import { updateSweetenerSystemTypeSchema } from 'validationSchema';

const Edit: FC = (): JSX.Element => {
  const navigation = useNavigate();
  const params = useParams<ParamType>();
  const { id } = params || {};

  const methods = useForm<AddSweetenerSystemFormType>({
    defaultValues: sweetenerSystemInitialValue,
    resolver: yupResolver(updateSweetenerSystemTypeSchema),
  });

  const { handleSubmit, setValue } = methods;

  const [createFlavor, { loading: CreateLoading }] = useCreateFlavorMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { createSweetener } = data;
      const { response } = createSweetener || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.CREATED) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [updateFlavor, { loading: UpdateLoading }] = useUpdateFlavorMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateSweetener } = data;
      const { response } = updateSweetener || {};
      const { status, message } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        navigation(-1);
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const [getFlavor, { loading: getLoading }] = useGetFlavorLazyQuery({
    onCompleted: (data) => {
      const { getSweetener } = data || {};
      const { response, sweetener } = getSweetener || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setValues(sweetener as SweetenerPayload['sweetener']);
      }
    },
    onError: () => {
      setValues(null);
    },
  });

  const onSubmit: SubmitHandler<AddSweetenerSystemFormType> = async (data) => {
    const { mgPerServing, costPerServing, name, nature, size, productSize, productType } = data || {};
    const { value: productSizeId } = productSize || {};
    const { value: productTypeId } = productType || {};

    if (id) {
      await updateFlavor({
        variables: {
          updateSweetenerInput: {
            id: id || '',
            name: name || '',
            size: size as SweetenerSizeType,
            nature: nature as SweetenerNatureType,
            type: SweetenerType.Flavor,
            costPerServing: parseFloat(costPerServing) || 0,
            mgPerServing: Number(mgPerServing),
            productTypeId: productTypeId || '',
            ...(productSizeId && { productSizeId: productSizeId }),
          },
        },
      });
    } else {
      await createFlavor({
        variables: {
          createSweetenerInput: {
            name: name || '',
            size: size as SweetenerSizeType,
            nature: nature as SweetenerNatureType,
            type: SweetenerType.Flavor,
            costPerServing: parseFloat(costPerServing) || 0,
            mgPerServing: Number(mgPerServing),
            productTypeId: productTypeId || '',
            ...(productSizeId && { productSizeId: productSizeId }),
          },
        },
      });
    }
  };

  const fetchFlavor = useCallback(async () => {
    id &&
      (await getFlavor({
        variables: {
          getSweetenerInput: {
            id,
          },
        },
      }));
  }, [id, getFlavor]);

  const setValues = (params: SweetenerPayload['sweetener']) => {
    const { costPerServing, name, mgPerServing, nature, size, type, productType, productSize } = params || {};

    const { name: productTypeName, id: productTypeId } = productType || {};
    const { name: productSizeName, id: productSizeId } = productSize || {};

    setValue('name', name || '');
    setValue('nature', nature || '');
    setValue('size', size || '');
    setValue('type', type || '');
    setValue('costPerServing', `${costPerServing ?? 0}`);
    setValue('mgPerServing', `${mgPerServing ?? 0}`);
    setValue('productSize', { value: productSizeId ?? '', name: productSizeName ?? '' });
    setValue('productType', { value: productTypeId ?? '', name: productTypeName ?? '' });
  };

  useEffect(() => {
    id && fetchFlavor();
  }, [id, fetchFlavor]);

  const loading = UpdateLoading || CreateLoading;

  return (
    <Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={flexCenterBetween}>
            <Typography variant="h5">{id ? EDIT_FLAVOR_TEXT : ADD_FLAVOR_TEXT}</Typography>
            {!id && (
              <Button variant="contained" type="submit">
                {ADD_TEXT}
              </Button>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item lg={6} md={12}>
              {getLoading ? (
                <DataLoader columns={12} rows={6} />
              ) : (
                <FlavorForm loading={loading} isEdit={!!id} />
              )}
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <BackdropLoader open={loading} text={id ? FLAVOR_IS_UPDATED_TEXT : FLAVOR_IS_CREATED_TEXT} />
    </Fragment>
  );
};

export default Edit;
